import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { ChevronDown, Search, Building2, LayoutGrid } from "lucide-react"

export type PropertyOption = {
  id: string
  name: string
  clientId?: string
  clientName?: string
}

/** Client option for "View combined by client" (value = "client:uuid"). */
export type ClientOption = {
  id: string
  name: string
  propertyCount: number
}

type PropertySelectorProps = {
  /** Properties for the dropdown (with optional client for grouping). */
  properties: PropertyOption[]
  /** Group options by client name (e.g. for creator or tenant super admin). */
  groupByClient?: boolean
  /** For admins: show "View combined by client" options (value = "client:clientId"). */
  clientOptions?: ClientOption[]
  /** Currently selected: null = All Properties, "client:uuid" = that client's combined view, or property id. */
  value: string | null
  onChange: (propertyId: string | null) => void
  placeholder?: string
  disabled?: boolean
  /** Accessible label. */
  "aria-label"?: string
}

const CLIENT_PREFIX = "client:"

export function PropertySelector({
  properties,
  groupByClient = false,
  clientOptions = [],
  value,
  onChange,
  placeholder = "All Properties",
  disabled = false,
  "aria-label": ariaLabel = "Filter by property",
}: PropertySelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const savedScrollTopRef = useRef(0)

  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current && target && !containerRef.current.contains(target) &&
        listboxRef.current && !listboxRef.current.contains(target)
      ) {
        if (listboxRef.current) savedScrollTopRef.current = listboxRef.current.scrollTop
        setOpen(false)
      }
    }
    document.addEventListener("click", handle, true)
    return () => document.removeEventListener("click", handle, true)
  }, [open])

  // Recompute dropdown position whenever it opens or on scroll/resize
  useEffect(() => {
    if (!open || !containerRef.current) return
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow
      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        minWidth: Math.min(320, window.innerWidth - 16),
        maxWidth: Math.min(window.innerWidth - 16, 384),
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top, top: "auto" }
          : { top: rect.bottom + 4, bottom: "auto" }),
        zIndex: 10001,
      })
    }
    update()
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [open])

  useEffect(() => {
    if (value === null) savedScrollTopRef.current = 0
  }, [value])

  useEffect(() => {
    if (!open || !listboxRef.current) return
    const el = listboxRef.current
    const scrollTo = savedScrollTopRef.current
    el.scrollTop = scrollTo
    if (scrollTo === 0) {
      const rafId = requestAnimationFrame(() => {
        el.scrollTop = 0
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [open])

  const searchLower = search.trim().toLowerCase()
  const filtered = searchLower
    ? properties.filter(
        (p) =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.clientName?.toLowerCase().includes(searchLower) ?? false)
      )
    : properties

  const selectedLabel =
    value === null
      ? placeholder
      : value.startsWith(CLIENT_PREFIX)
        ? (clientOptions.find((c) => c.id === value.slice(CLIENT_PREFIX.length))?.name ?? "Client") + " (all properties)"
        : properties.find((p) => p.id === value)?.name ?? placeholder

  /** When groupByClient: key = clientId (or "other"), value = { clientName, props }. */
  const byClient = new Map<string, { clientName: string; props: PropertyOption[] }>()
  if (groupByClient) {
    filtered.forEach((p) => {
      const cid = p.clientId ?? "other"
      const cname = p.clientName ?? "Other"
      if (!byClient.has(cid)) byClient.set(cid, { clientName: cname, props: [] })
      byClient.get(cid)!.props.push(p)
    })
  }

  const handleSelect = (id: string | null) => {
    if (listboxRef.current) savedScrollTopRef.current = listboxRef.current.scrollTop
    onChange(id)
    setSearch("")
    requestAnimationFrame(() => {
      setOpen(false)
    })
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-xs">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white/80 text-left font-mono text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/30 disabled:opacity-50"
      >
        <Building2 className="w-3.5 h-3.5 shrink-0 text-white/30" aria-hidden />
        <span className="truncate flex-1">{selectedLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-white/30 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open && createPortal(
        <div
          ref={listboxRef}
          role="listbox"
          aria-label={ariaLabel}
          style={dropdownStyle}
          className="max-h-[320px] overflow-auto rounded-xl border border-white/[0.08] bg-black/80 backdrop-blur-2xl shadow-xl py-1"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-xl px-2 pb-1 border-b border-white/[0.06]">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.04]">
              <Search className="w-3.5 h-3.5 text-white/30 shrink-0" aria-hidden />
              <input
                type="text"
                inputMode="search"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search properties…"
                aria-label="Search properties"
                className="flex-1 min-w-0 bg-transparent font-mono text-[13px] text-white/80 placeholder:text-white/30 focus:outline-none"
                autoFocus
              />
            </div>
          </div>

          {groupByClient && byClient.size > 0 ? (
            Array.from(byClient.entries()).map(([clientId, { clientName, props }]) => {
              const clientValue = clientId !== "other" ? CLIENT_PREFIX + clientId : null
              const canSelectClient = clientValue !== null && clientOptions.some((c) => c.id === clientId)
              return (
              <div key={clientId}>
                {canSelectClient ? (
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === clientValue}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelect(clientValue)
                    }}
                    className="w-full px-3 py-1.5 mt-1 first:mt-0 flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-white/40 bg-white/[0.02] border-y border-white/[0.06] hover:bg-white/[0.06] focus:outline-none focus:bg-white/[0.06] cursor-pointer transition-colors text-left"
                  >
                    <span className="text-amber-400">Client</span>
                    <span className="truncate flex-1">{clientName}</span>
                    <LayoutGrid className="w-3 h-3 shrink-0 text-white/20" aria-hidden />
                    <span className="text-[10px] normal-case font-mono text-white/30 shrink-0">View all</span>
                  </button>
                ) : (
                  <div
                    className="px-3 py-1.5 mt-1 first:mt-0 flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-white/30 bg-white/[0.02] border-y border-white/[0.06]"
                    aria-hidden
                  >
                    <span className="text-amber-400">Client</span>
                    <span className="truncate">{clientName}</span>
                  </div>
                )}
                <div className="pb-0.5" role="group" aria-label={`Properties for ${clientName}`}>
                  {props.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      role="option"
                      aria-selected={value === p.id}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSelect(p.id)
                      }}
                      className="w-full text-left pl-4 pr-3 py-2 font-mono text-[13px] text-white/80 hover:bg-white/[0.06] focus:outline-none focus:bg-white/[0.06] flex items-center gap-2"
                    >
                      <span className="text-white/30 font-mono text-[10px] font-bold uppercase tracking-wider w-12 shrink-0">Property</span>
                      <span className="truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              )
            })
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={value === p.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSelect(p.id)
                }}
                className="w-full text-left px-3 py-2.5 font-mono text-[13px] text-white/80 hover:bg-white/[0.06] focus:outline-none focus:bg-white/[0.06] flex items-center gap-2"
              >
                {p.clientName ? (
                  <>
                    <span className="text-white/30 font-mono text-[10px] font-bold uppercase tracking-wider shrink-0">Property</span>
                    <span className="truncate">{p.name}</span>
                    <span className="text-white/30 font-mono text-[10px] truncate ml-auto">({p.clientName})</span>
                  </>
                ) : (
                  <span className="truncate">{p.name}</span>
                )}
              </button>
            ))
          )}

          {filtered.length === 0 && searchLower && (
            <div className="px-3 py-4 font-mono text-[13px] text-white/40 text-center">
              No properties match
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
